import { useRef, useCallback, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';

/**
 * A hook that works like useLazyQuery but with debouncing functionality
 *
 * @param {DocumentNode} query - The GraphQL query document
 * @param {Object} options - Apollo useLazyQuery options
 * @param {number} debounceTimeMs - Debounce delay in milliseconds
 * @returns {Array} - [debouncedLazyQuery, queryResult] same as useLazyQuery
 */
export const useDebouncedLazyQuery = (
  query,
  options = {},
  debounceTimeMs = 300
) => {
  const [executeLazyQuery, queryResult] = useLazyQuery(query, options);

  const timeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  const debouncedLazyQuery = useCallback(
    (queryOptions = {}) => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Cancel any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      // Create new abort controller for the upcoming request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Return a promise that resolves/rejects based on the debounced query
      return new Promise((resolve, reject) => {
        timeoutRef.current = setTimeout(async () => {
          try {
            // Check if this request was cancelled
            if (abortController.signal.aborted) {
              reject(new Error('Request was cancelled'));
              return;
            }

            // Execute the query with the abort signal
            const result = await executeLazyQuery({
              ...queryOptions,
              context: {
                ...queryOptions.context,
                fetchOptions: {
                  ...queryOptions.context?.fetchOptions,
                  signal: abortController.signal,
                },
              },
            });

            // Check again if cancelled after execution
            if (abortController.signal.aborted) {
              reject(new Error('Request was cancelled'));
              return;
            }

            resolve(result);
          } catch (error) {
            if (abortController.signal.aborted) {
              reject(new Error('Request was cancelled'));
            } else {
              reject(error);
            }
          } finally {
            // Clean up references if this was the current request
            if (abortControllerRef.current === abortController) {
              abortControllerRef.current = null;
            }
          }
        }, debounceTimeMs);
      });
    },
    [executeLazyQuery, debounceTimeMs]
  );

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return [debouncedLazyQuery, queryResult];
};
